import React from "react";
import { useTranslation } from "react-i18next";
import "../assets/styles/TerraformArticle.scss";

function MonitoringArticle() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  if (isEn) {
    return (
      <div className="terraform-article">
        <h1>The Importance of Monitoring in Infrastructure</h1>
        <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
          Observability · Alerting · SRE — April 2025
        </p>

        <h2>Why Monitoring is Critical</h2>
        <p>
          You can't manage what you don't measure. This rule, simple in appearance, is at the
          heart of any healthy infrastructure. Monitoring answers three fundamental questions:{" "}
          <strong>is it working?</strong>,{" "}
          <strong>is it about to fail?</strong>, and{" "}
          <strong>why did it fail?</strong>
        </p>
        <p>
          Without monitoring, you operate blind. You wait for a user to report a problem before
          discovering it. With properly configured monitoring, you detect anomalies before they
          impact end users.
        </p>

        <h2>The 4 Golden Signals (Google SRE)</h2>
        <p>
          Google's SRE team defined 4 fundamental metrics to monitor on any system:
        </p>
        <ul>
          <li>
            <strong>Latency</strong> — request response time. You must distinguish the latency
            of successful requests from that of failed requests.
          </li>
          <li>
            <strong>Traffic</strong> — demand on the system (requests/second, messages/second).
            Indicator of load and activity.
          </li>
          <li>
            <strong>Error rate</strong> — the proportion of requests that fail (HTTP 5xx,
            exceptions, timeouts).
          </li>
          <li>
            <strong>Saturation</strong> — how "full" the system is (CPU, memory, disk, queue).
            Predictive indicator of degradation.
          </li>
        </ul>

        <h2>Modern Monitoring Stack: Prometheus & Grafana</h2>
        <p>
          The Prometheus + Grafana stack has become the de facto standard for cloud-native
          infrastructure monitoring. Here's how it works:
        </p>
        <ul>
          <li>
            <strong>Prometheus</strong> — collects metrics in pull mode from exporters
            (node_exporter, kube-state-metrics, blackbox_exporter...) and stores them in its
            time-series database.
          </li>
          <li>
            <strong>Alertmanager</strong> — receives alerts from Prometheus and routes them to
            appropriate channels (Slack, PagerDuty, email) with deduplication and silencing.
          </li>
          <li>
            <strong>Grafana</strong> — visualizes Prometheus metrics as dashboards. Also supports
            Loki (logs) and Tempo (traces).
          </li>
        </ul>
        <pre><code>{`# Example Prometheus alert rule
groups:
  - name: infrastructure
    rules:
      - alert: HighCPUUsage
        expr: 100 - (avg by(instance) (rate(node_cpu_seconds_total{mode="idle"}[5m])) * 100) > 85
        for: 5m
        labels:
          severity: warning
        annotations:
          summary: "High CPU on {{ $labels.instance }}"
          description: "CPU at {{ $value }}% for 5 minutes"

      - alert: DiskSpaceLow
        expr: (node_filesystem_avail_bytes / node_filesystem_size_bytes) * 100 < 15
        for: 10m
        labels:
          severity: critical
        annotations:
          summary: "Critical disk space on {{ $labels.instance }}"
          description: "Only {{ $value }}% free space on {{ $labels.mountpoint }}"`}</code></pre>

        <h2>Zabbix for On-Premise Infrastructure</h2>
        <p>
          For hybrid or on-premise infrastructures, Zabbix remains a reference. It covers
          network (SNMP, ICMP), system (Zabbix agents) and application monitoring, with a
          centralized management interface.
        </p>
        <p>
          In my context, I use Zabbix to monitor physical equipment (switches, Fortinet firewalls)
          and Proxmox VMs, and Prometheus/Grafana for Kubernetes workloads. The two complement
          each other naturally.
        </p>

        <h2>Security Monitoring: SIEM and XDR</h2>
        <p>
          Monitoring is not limited to performance. Security monitoring is equally critical. Two
          categories of tools stand out:
        </p>
        <ul>
          <li>
            <strong>SIEM (Security Information and Event Management)</strong> — centralizes and
            correlates security logs to detect abnormal behavior.{" "}
            <em>Sekoia.io</em> is a French SIEM/SOAR platform that enables automated incident
            response.
          </li>
          <li>
            <strong>XDR (Extended Detection and Response)</strong> — extends detection beyond
            endpoints to the network, cloud and identities. <em>SentinelOne</em> is an example
            of an XDR agent that protects endpoints and reports events to a centralized console.
          </li>
        </ul>

        <h2>Alerting Best Practices</h2>
        <p>
          Poorly configured monitoring generates noise and creates alert fatigue. Teams end up
          ignoring alerts — including critical ones. A few rules:
        </p>
        <ul>
          <li>Every alert must be <strong>actionable</strong> — if no one knows what to do, delete the alert</li>
          <li>Define realistic thresholds based on historical data, not arbitrary values</li>
          <li>Separate <em>urgent</em> alerts (pager) from <em>informational</em> alerts (ticket)</li>
          <li>Document each alert with a runbook: what to do, who to call</li>
          <li>Regularly review alerts that never trigger — they may be unnecessary</li>
        </ul>

        <h2>Observability vs Monitoring</h2>
        <p>
          Monitoring measures known states in advance (<em>"is the CPU high?"</em>).{" "}
          <strong>Observability</strong> goes further: it allows understanding any internal state
          of the system from the data it emits, even unknown states.
        </p>
        <p>
          Observability relies on three pillars: <strong>metrics</strong> (Prometheus),{" "}
          <strong>logs</strong> (Loki, ELK) and <strong>distributed traces</strong> (Jaeger,
          Tempo). Together, they provide a complete view of the behavior of a distributed system.
        </p>

        <h2>Conclusion</h2>
        <p>
          Monitoring is the life insurance of infrastructure. Investing in a robust monitoring
          stack means gaining operational peace of mind, reducing MTTR (Mean Time To Recovery)
          and anticipating incidents before they become crises. In a hybrid cloud + on-premise
          environment, combining Prometheus, Grafana, Zabbix and security tools like Sekoia and
          SentinelOne provides complete coverage — from performance to security events.
        </p>
      </div>
    );
  }

  return (
    <div className="terraform-article">
      <h1>L'importance du Monitoring en infrastructure</h1>
      <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
        Observabilité · Alerting · SRE — Avril 2025
      </p>

      <h2>Pourquoi le monitoring est critique</h2>
      <p>
        Vous ne pouvez pas gérer ce que vous ne mesurez pas. Cette règle, simple en apparence,
        est au cœur de toute infrastructure saine. Le monitoring permet de répondre à trois
        questions fondamentales : <strong>est-ce que ça fonctionne ?</strong>,{" "}
        <strong>est-ce que ça va tomber ?</strong>, et{" "}
        <strong>pourquoi est-ce que ça a tombé ?</strong>
      </p>
      <p>
        Sans monitoring, on opère à l'aveugle. On attend qu'un utilisateur signale un problème
        pour le découvrir. Avec un monitoring bien configuré, on détecte les anomalies avant
        qu'elles impactent les utilisateurs finaux.
      </p>

      <h2>Les 4 signaux dorés (Google SRE)</h2>
      <p>
        L'équipe SRE de Google a défini 4 métriques fondamentales à surveiller sur n'importe
        quel système :
      </p>
      <ul>
        <li>
          <strong>Latence</strong> — le temps de réponse des requêtes. Il faut distinguer la
          latence des requêtes réussies de celle des requêtes en erreur.
        </li>
        <li>
          <strong>Trafic</strong> — la demande sur le système (requêtes/seconde, messages/seconde).
          Indicateur de charge et d'activité.
        </li>
        <li>
          <strong>Taux d'erreur</strong> — la proportion de requêtes qui échouent (HTTP 5xx,
          exceptions, timeouts).
        </li>
        <li>
          <strong>Saturation</strong> — à quel point le système est "plein" (CPU, mémoire,
          disque, file d'attente). Indicateur prédictif de dégradation.
        </li>
      </ul>

      <h2>Stack de monitoring moderne : Prometheus & Grafana</h2>
      <p>
        La stack Prometheus + Grafana est devenue le standard de facto pour le monitoring
        d'infrastructures cloud-native. Voici comment elle s'articule :
      </p>
      <ul>
        <li>
          <strong>Prometheus</strong> — collecte les métriques en mode pull depuis les
          exporters (node_exporter, kube-state-metrics, blackbox_exporter...) et les stocke
          dans sa base de données time-series.
        </li>
        <li>
          <strong>Alertmanager</strong> — reçoit les alertes de Prometheus et les route vers
          les canaux appropriés (Slack, PagerDuty, email) avec déduplication et silencing.
        </li>
        <li>
          <strong>Grafana</strong> — visualise les métriques Prometheus sous forme de
          dashboards. Supporte aussi Loki (logs) et Tempo (traces).
        </li>
      </ul>
      <pre><code>{`# Exemple de règle d'alerte Prometheus
groups:
  - name: infrastructure
    rules:
      - alert: HighCPUUsage
        expr: 100 - (avg by(instance) (rate(node_cpu_seconds_total{mode="idle"}[5m])) * 100) > 85
        for: 5m
        labels:
          severity: warning
        annotations:
          summary: "CPU élevé sur {{ $labels.instance }}"
          description: "CPU à {{ $value }}% depuis 5 minutes"

      - alert: DiskSpaceLow
        expr: (node_filesystem_avail_bytes / node_filesystem_size_bytes) * 100 < 15
        for: 10m
        labels:
          severity: critical
        annotations:
          summary: "Espace disque critique sur {{ $labels.instance }}"
          description: "Seulement {{ $value }}% d'espace libre sur {{ $labels.mountpoint }}"`}</code></pre>

      <h2>Zabbix pour l'infrastructure on-premise</h2>
      <p>
        Pour les infrastructures hybrides ou on-premise, Zabbix reste une référence. Il couvre
        la supervision réseau (SNMP, ICMP), système (agents Zabbix) et applicative, avec une
        interface de gestion centralisée.
      </p>
      <p>
        Dans mon contexte, j'utilise Zabbix pour superviser les équipements physiques (switches,
        firewalls Fortinet) et les VMs Proxmox, et Prometheus/Grafana pour les workloads
        Kubernetes. Les deux se complètent naturellement.
      </p>

      <h2>Monitoring de sécurité : SIEM et XDR</h2>
      <p>
        Le monitoring ne se limite pas aux performances. La surveillance de sécurité est tout
        aussi critique. Deux catégories d'outils se distinguent :
      </p>
      <ul>
        <li>
          <strong>SIEM (Security Information and Event Management)</strong> — centralise et
          corrèle les logs de sécurité pour détecter des comportements anormaux.{" "}
          <em>Sekoia.io</em> est une plateforme SIEM/SOAR française qui permet d'automatiser
          la réponse aux incidents.
        </li>
        <li>
          <strong>XDR (Extended Detection and Response)</strong> — étend la détection au-delà
          des endpoints vers le réseau, le cloud et les identités. <em>SentinelOne</em> est
          un exemple d'agent XDR qui protège les endpoints et remonte les événements dans
          une console centralisée.
        </li>
      </ul>

      <h2>Les bonnes pratiques d'alerting</h2>
      <p>
        Un monitoring mal configuré génère du bruit et crée de la fatigue d'alerte. Les
        équipes finissent par ignorer les alertes — y compris les critiques. Quelques règles :
      </p>
      <ul>
        <li>Chaque alerte doit être <strong>actionnable</strong> — si personne ne sait quoi faire, supprimez l'alerte</li>
        <li>Définir des seuils réalistes basés sur des données historiques, pas des valeurs arbitraires</li>
        <li>Séparer les alertes <em>urgentes</em> (pager) des alertes <em>informatives</em> (ticket)</li>
        <li>Documenter chaque alerte avec un runbook : que faire, qui appeler</li>
        <li>Réviser régulièrement les alertes qui ne se déclenchent jamais — elles sont peut-être inutiles</li>
      </ul>

      <h2>Observabilité vs Monitoring</h2>
      <p>
        Le monitoring mesure des états connus à l'avance (<em>"est-ce que le CPU est élevé ?"</em>).
        L'<strong>observabilité</strong> va plus loin : elle permet de comprendre n'importe quel
        état interne du système à partir des données qu'il émet, même des états inconnus.
      </p>
      <p>
        L'observabilité repose sur trois piliers : les <strong>métriques</strong> (Prometheus),
        les <strong>logs</strong> (Loki, ELK) et les <strong>traces distribuées</strong> (Jaeger,
        Tempo). Ensemble, ils donnent une vue complète du comportement d'un système distribué.
      </p>

      <h2>Conclusion</h2>
      <p>
        Le monitoring est l'assurance vie de l'infrastructure. Investir dans une stack de
        supervision robuste, c'est gagner en sérénité opérationnelle, réduire le MTTR
        (Mean Time To Recovery) et anticiper les incidents avant qu'ils deviennent des crises.
        Dans un environnement hybride cloud + on-premise, combiner Prometheus, Grafana, Zabbix
        et des outils de sécurité comme Sekoia et SentinelOne offre une couverture complète —
        des performances aux événements de sécurité.
      </p>
    </div>
  );
}

export default MonitoringArticle;
